import { Link } from '@inertiajs/react'
import { ArrowLeft, Check } from 'lucide-react'
import type { ReactNode } from 'react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import TechCta from './TechCta'
import TechSubnav from './TechSubnav'
import type { TechnologyDetailProps } from '@/types/technology'

/* The shell for a detail page — a child of a sub-page, not a sixth sibling.
 *
 * The builder and the royalty analytics carry more than a module card holds,
 * and stacking both full mocks on the Distribution page made it enormously
 * tall for a reader who only wanted the capability list. They get pages here
 * instead, reached from a wireframe and a thumbnail on the parent.
 *
 * The subnav still shows the PARENT as current, because that is where these
 * live in the section's structure and a reader should not lose their place by
 * following a link one level down. The breadcrumb is what says how deep they
 * are, and it carries the parent as a real link back.
 */
export default function TechnologyDetailPage({
  hero,
  breadcrumbs,
  sections,
  back,
  subnav,
  brand,
  cta,
  visual,
}: TechnologyDetailProps & { visual: ReactNode }) {
  return (
    <>
      <AtmosphericHero minHeight="42vh">
        <div className="mx-auto max-w-3xl text-center">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[0.75rem]">
              {breadcrumbs.map((crumb, index) => (
                <li key={crumb.label} className="flex items-center gap-2">
                  {index > 0 && (
                    <span className="text-white/20" aria-hidden="true">
                      /
                    </span>
                  )}
                  {crumb.href ? (
                    <Link
                      href={crumb.href}
                      prefetch
                      className="text-white/45 no-underline transition-colors hover:text-white/80"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-white/70" aria-current="page">
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>

          <ScrollReveal>
            <p className="marketing-eyebrow-mono justify-center">{hero.eyebrow}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.06}>
            <h1 className="hero-headline mt-5" style={{ fontSize: 'clamp(1.9rem, 4.2vw, 3.2rem)' }}>
              {hero.title}
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.12}>
            <p className="marketing-lead mx-auto mt-6 max-w-2xl">{hero.body}</p>
          </ScrollReveal>
        </div>
      </AtmosphericHero>

      <TechSubnav items={subnav} brand={brand} />

      {/* The full mock, which is why this page exists. */}
      <section className="container mx-auto px-6 pt-14 lg:pt-16">
        <ScrollReveal>
          <div className="mx-auto max-w-5xl">{visual}</div>
        </ScrollReveal>
      </section>

      {/* The reasoning behind it. */}
      <section className="container mx-auto px-6 py-16 lg:py-20">
        <div className="mx-auto flex max-w-4xl flex-col gap-5">
          {sections.map((section, index) => (
            <ScrollReveal key={section.heading} delay={Math.min(index, 3) * 0.05}>
              <article className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-6 sm:p-9">
                <h2
                  className="text-xl font-semibold text-white sm:text-2xl"
                  style={{ fontFamily: 'var(--wavbee-font-display)', letterSpacing: '-0.02em' }}
                >
                  {section.heading}
                </h2>
                <p className="mt-4 text-[0.95rem] leading-[1.85] text-white/70">{section.body}</p>
                <ul className="mt-6 flex flex-col gap-3 border-t border-white/[0.07] pt-5">
                  {section.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <Check
                        className="mt-[0.2rem] h-4 w-4 shrink-0 text-[#ff6a1a]"
                        aria-hidden="true"
                      />
                      <span className="text-[0.875rem] leading-[1.7] text-white/60">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </ScrollReveal>
          ))}

          <ScrollReveal>
            <Link
              href={back.href}
              prefetch
              className="marketing-inline-link inline-flex items-center gap-2 text-[0.875rem] font-semibold hover:!text-[#ff6a1a]"
            >
              <ArrowLeft className="h-4 w-4" />
              {back.label}
            </Link>
          </ScrollReveal>
        </div>
      </section>

      <TechCta cta={cta} />
    </>
  )
}
